import { SaveOutlined } from '@ant-design/icons';
import { Checkbox, Col, Form, Input, Modal, Row, Spin } from 'antd';
import React, { useState } from 'react';
import ZTable, { ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER } from '@/config';
import { defaultApiErrorHandle } from '@/helpers/errorHandler';
import { CompanyUserDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const columns: IColumnType<CompanyUserDto>[] = [
  {
    title: 'companyRolesPage_RoleName',
    dataIndex: 'name',
    ellipsis: true,
  },
  {
    title: 'companyRolesPage_NormalizedName',
    dataIndex: 'normalizedName',
    ellipsis: true,
  },
];

const GlobalRolesModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissions, setPermissions] = React.useState<Array<{ value: any; label: any }>>([]);

  React.useEffect(() => {
    apiSdk.RoleService.getGlobalPermissions().then(res => {
      setPermissions(res?.map(x => ({ value: x, label: x })) || []);
    });
  }, []);

  const updateEntity = values => {
    setLoading(true);
    apiSdk.RoleService.updateGlobalRole({ body: { ...values } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          defaultApiErrorHandle(response);
        }
      })
      .catch(error => {
        defaultApiErrorHandle(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    apiSdk.RoleService.getRoleDetails({ id: id })
      .then(res => {
        if (res) {
          setFormVisible(true);
          form.setFieldsValue({
            ...res,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      open={true}
      maskClosable={false}
      width={ModalWidth.large}
      className="nta-custom-form"
      title={'Manage Global Roles'}
      forceRender={true}
      style={{ top: 10 }}
      cancelText={t('close')}
      okButtonProps={{ style: { display: 'none' } }}
      onCancel={onClose}
    >
      <div className="ant-layout-content-body">
        <ZTable<CompanyUserDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/user-accounts/roles/get-global-roles"
          bordered
          order_by="name"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            onEdit(record.id || '');
          }}
          allowDelete={false}
          transformDataSource={d => ({ data: d })}
        />
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.small}
            className="nta-custom-form"
            title={t('companyRolesPage_UpdateRole')}
            forceRender={true}
            style={{ top: 10 }}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  updateEntity(values);
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form form={form} size="middle" layout="horizontal" labelAlign="right">
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={ROW_GUTTER}>
                  <Col md={24}>
                    <Form.Item name="name">
                      <Input disabled className="input-value-color" />
                    </Form.Item>
                  </Col>
                  <Col md={24}>
                    <b>{t('companyRolesPage_Permissions')}</b>
                    <Form.Item name="permissions">
                      <Checkbox.Group>
                        <Row gutter={ROW_GUTTER}>
                          {permissions.map(p => {
                            return (
                              <Col md={12} key={p.value}>
                                <Checkbox value={p.value}>{p.label}</Checkbox>
                              </Col>
                            );
                          })}
                        </Row>
                      </Checkbox.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </Modal>
  );
};
export default GlobalRolesModal;
